import React from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Col, Image, Row } from '@thd-olt-component-react/core-ui';
import { useNavigate } from '@thd-olt-component-react/router';
import { useConfigService } from '@thd-nucleus/experience-context';
import DashboardTable from '../../../../components/DashboardTable/DashboardTable';
import { useStore } from '../../../../store';
import JobApplicantActionDropdown from '../../../../components/ActionDropdowns/JobApplicantActionDropdown';
import { ProfileImageWithFavoriteIcon } from '../../../../components/common/ProfileImageWithFavoriteIcon/ProfileImageWithFavoriteIcon';
import { APPLICANT_STATUS_JSX_MAP, EXP_ROUTES } from '../../../../utils/constants';
import { clickEvents } from '../../../../utils/analytics';
import { generateImageSource } from '../../../../utils/generateImageSource';
import OverflowTooltip from '../../../../components/common/OverflowTooltip/OverflowTooltip';

const _JobApplicantsDesktop = () => {

  const navigate = useNavigate();
  const isHiredEnabled = useConfigService('fs:isHiredEnabled');

  const {
    jobApplicantStore: {
      jobApplicants,
      jobPostingMetadata,
      sort,
      setSort,
      setFilter,
      filter,
      setPage
    }
  } = useStore();

  const columnHeaders = [
    {
      value: '',
      key: 'profilePicture',
      testId: 'job-applicant-profile-picture'
    },
    {
      value: 'Name',
      key: 'name',
      sortable: true,
      testId: 'job-applicant-name'
    },
    {
      value: 'Trade Type',
      key: 'tradeType',
      testId: 'job-applicant-trade-type'
    },
    {
      value: 'Experience Level',
      key: 'yearsOfExperience',
      testId: 'job-applicant-experiene-level'
    },
    {
      value: 'Location',
      key: 'location',
      sortable: true,
      testId: 'job-applicant-location'
    },
    {
      value: 'Status',
      key: 'status',
      sortable: true,
      testId: 'job-applicant-status',
      items: {
        type: 'radio',
        fallback: 'applied',
        values: [
          {
            value: 'Show All',
            key: 'showAll',
            show: true
          },
          {
            value: 'Invited',
            key: 'invited',
            show: true
          },
          {
            value: 'Applied',
            key: 'applied',
            show: true
          },
          {
            value: 'Hired',
            key: 'hired',
            show: isHiredEnabled
          },
          {
            value: 'Not Hired',
            key: 'not_hired',
            show: isHiredEnabled
          },
          {
            value: 'Declined',
            key: 'declined',
            show: true
          }
        ]
      }
    },
    {
      value: 'Actions',
      key: 'actions'
    }
  ];

  /* change sort handler for inner components click events */
  const handleSortChange = ({ by, order }) => {
    clickEvents.jobPostingsApplicantsSortClick(by);
    setSort({ by, order });
  };

  const handleFilterChange = (by) => {
    clickEvents.jobPostingsApplicantsSortClick(by);
    setFilter(by);
    setPage(1);
  };

  const enrichData = (data) => {
    const enrichedData = [];
    data.forEach((row) => {
      const enrichedRow = { ...row };
      enrichedRow.name = (
        <OverflowTooltip
          className="p2p-job-posting-table__title"
          byHeight
        >
          <span style={{ color: '#286FAD' }}>{row.name}</span>
        </OverflowTooltip>
      );
      enrichedRow.tradeType = <span>{row.tradeTypes && row.tradeTypes.join(', ')} </span>;
      enrichedRow.location = row.city && row.state ? <span>{row.city + ', ' + row.state} </span> : '';
      enrichedRow.status = APPLICANT_STATUS_JSX_MAP[row.status];
      enrichedRow.profilePicture = (
        <div className="ptp-table-profile-image">
          <ProfileImageWithFavoriteIcon
            candidate={row}
          />
        </div>
      );
      enrichedRow.onRowClick = () => navigate(EXP_ROUTES.APPLICANTS_PROFILE(jobPostingMetadata.jobPostingId, row.candidateId));
      enrichedRow.actions = <JobApplicantActionDropdown applicant={row} />;
      enrichedData.push(enrichedRow);
    });
    return enrichedData;
  };

  const renderJobApplicantsSubtext = () => {

    if (jobApplicants?.length > 0) {
      return (
        <>
          The following list of people have been invited or applied to your job posting:{' '}
          <span
            className="job-applicants__describtion-job-name"
          >{jobPostingMetadata.title || ''}
          </span>.<br />
          To view their detail, simply click on the applicant. You can also manage the pool using
          the Action column far right.
        </>
      );
    }
    return (
      <>
            You don’t have any job applicants yet. While you’re waiting for candidates to apply, try browsing our
            pool of qualified skilled trades people.
            You can call or email any candidate, even if they haven’t applied.
        <Button
          style={{ marginTop: '17px', width: '225px' }}
          data-testid="find-candidates"
          onClick={() => { navigate(EXP_ROUTES.CANDIDATE_SEARCH); }}
        >
              Find Candidates
        </Button>
      </>
    );

  };

  const renderJobApplicants = () => {
    return (
      <>
        <Row>
          <Col xs="8">
            <div className="ptp-job-posting__sub-title">
              <Image
                src={generateImageSource('/images/v1/path-to-pro/job-applicants-icon.svg')}
                className="ptp-job-posting__job-applicants-icon"
                alt="job applicant header icon"
              />
              Job Applicants
            </div>

            <p className="job-applicants__header__description">
              {renderJobApplicantsSubtext()}
            </p>
          </Col>
        </Row>
        {(jobPostingMetadata?.candidateTotalCount > 0 || jobApplicants?.length > 0)
          && (
            <DashboardTable
              data={enrichData(jobApplicants)}
              columnHeaders={columnHeaders}
              sort={sort}
              filter={filter}
              handleSortChange={handleSortChange}
              handleFilterChange={handleFilterChange}
            />
          )}
      </>
    );
  };

  return (
    <>
      {renderJobApplicants()}
    </>
  );
};

const JobApplicantsDesktop = observer(_JobApplicantsDesktop);
export default JobApplicantsDesktop;
